import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IRootState } from "../../../../../../reducer/CombineReducer";
import { IFieldDetails } from "../../../CreateSchedule/redux/Types";
import { CreateScheduleSlice } from "../../../CreateSchedule/redux";
import {
  convertDateToView,
  getCustomMaxDateValidation,
  GetDefaultScheduleData,
  getFormattedDate,
  setStartAndEndDate,
} from "../../../Helper/Script";
import { EmptyFieldValidation, getEndByDateDefaultValue, IsDateValid } from "../../ScheduleValidation";
import { DEFAULT_OCCURENCES, END_BY, END_AFTER, START_DATE, END_DATE } from "../../../Helper/Constants";
import ScheduleCreationFormView from "./ScheduleCreationForm.view";

export interface IValidationType {
  isInvalid: boolean;
  validationText: string;
}

/**
 * Responsible for rendering the Schedule Configuration Form onChange events
 */
const ScheduleCreationForm = (): JSX.Element => {

  const scheduling = useSelector((state: IRootState) => state.scheduling);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const { schedulingDetails } = scheduling;
  const { recurrenceType } = schedulingDetails;
  const startDateToCalculateEnd = schedulingDetails.recurrenceRangeStart;
  const [recurrenceRangeStartDate, setrecurrenceRangeStartDate] = useState({
    "value": new Date(),
    "valid": false,
    "errorText": ""
  });
  const endDate = new Date().setDate(new Date().getDate() + 1);
  const [recurrenceRangeEndDate, setrecurrenceRangeEndDate] = useState<IFieldDetails>({
    "value": endDate,
    "valid": false,
    "errorText": ""
  });
  const [noOfOccurences, setNoOfOccurences] = useState<IFieldDetails>({
    "value": DEFAULT_OCCURENCES,
    "valid": false,
    "errorText": ""
  });
  const isValidSchedule = scheduling.isValidScheduleName;
  const defaultEndDateValue = schedulingDetails.recurrenceRangeEnd ?
    convertDateToView(schedulingDetails.recurrenceRangeEnd) :
    getEndByDateDefaultValue(schedulingDetails.recurrenceRangeStart, recurrenceType);

  /**
   * Clearing the error Text if all the data are correct and used in recur type change
   */
  const clearErrorText = (): void => {
    setrecurrenceRangeEndDate({
      "value": "",
      "valid": false,
      "errorText": ""
    });
    setNoOfOccurences({
      "value": DEFAULT_OCCURENCES,
      "valid": false,
      "errorText": ""
    });
    setrecurrenceRangeStartDate({
      "value": new Date(),
      "valid": false,
      "errorText": ""
    });
  };

  /**
   * Schedule Name, Active in, End After form fields onChange event will be called here.
   * Newly selected or entered value will be updated in redux
   * End After - Value will be validated between 0-99
   */
  const onFieldChange = (e: React.SyntheticEvent<any, Event>): void => {
    const { id, value } = e.currentTarget;
    switch (id) {
      case "scheduleName": {
        dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
          ...scheduling.schedulingDetails,
          scheduleName: value,
        }));
        dispatch(CreateScheduleSlice.actions.setIsValidScheduleName(true));
        break;
      }
      case "endAfterValue": {
        const { isInvalid, validationText } = EmptyFieldValidation(value, translate, "endAfterValue");
        setNoOfOccurences({
          "value": value,
          "valid": isInvalid,
          "errorText": validationText
        });
        dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
          ...scheduling.schedulingDetails,
          recurrenceNoOfOccurrences: value,
        }));
        break;
      }
    }
  };

  /**
   * Recurrence Type onChange event
   * Based on recurrence type selection default value will be constructed
   * Constructed default value will be updated in redux
   */
  const onRecurTypeChange = (selectedValue: string): void => {
    clearErrorText();
    const details = GetDefaultScheduleData(selectedValue, scheduling.schedulingDetails);
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails(details));
  };

  /**
   * Recurrence Time on change event
   * Selected time value will be updated in redux
   */
  const onTimeChange = (selectedValue: string): void => {
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
      ...scheduling.schedulingDetails,
      recurrenceRunTime: selectedValue,
    }));
  };

  /**
   * Start Date Time on change event
   * Validation - Start date should be greater than current date and should be valid date
   * Selected date will be updated in redux
   */
  const onStartDateSelection = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const selectedDate = new Date(e.currentTarget.value);
    const startDate = getFormattedDate(selectedDate, false);
    const defaultEndDate = new Date(getEndByDateDefaultValue(startDate, schedulingDetails.recurrenceType));
    const endRecurrenceDate = getFormattedDate(defaultEndDate, false);
    if (selectedDate) {
      const { isInvalid, validationText } = IsDateValid(selectedDate, translate, START_DATE);
      setrecurrenceRangeStartDate({
        "value": selectedDate,
        "valid": isInvalid,
        "errorText": validationText
      });

      /*  Temporary if condition will be removed once the datepicker component is ready in emis ui,
      as this validation will be within the component. */
      if(getCustomMaxDateValidation(selectedDate)) {
        setrecurrenceRangeStartDate({
          "value": selectedDate,
          "valid": false,
          "errorText": translate("Scheduling.ScheduleErrors.MaxYearValidation")
        });
      }

      setStartAndEndDate(scheduling,
        dispatch,
        validationText,
        selectedDate,
        endRecurrenceDate);
    }
  };

  /**
   * EndBy  radio button on change event
   * If EndBy radio button selected, EndAfter text input will be disabled by setting state to false
   * If EndAfter radio button selected, EndBy (recurrenceRangeEnd) date picker will be disabled by 
   *   setting state to false
   */
  const onEndByRadioChange = (selectedValue: string): void => {
    if (selectedValue === END_BY) {
      dispatch(CreateScheduleSlice.actions.setIsEndAfterEnabled(false));
      dispatch(CreateScheduleSlice.actions.setIsEndByEnabled(true));
      if (scheduling.schedulingDetails.recurrenceNoOfOccurrences) {
        dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
          ...scheduling.schedulingDetails,
          recurrenceNoOfOccurrences: "",
          recurrenceRangeEnd: getFormattedDate(new Date(endDate), false)
        }));
      }
      if (!noOfOccurences.valid) {
        setNoOfOccurences({
          "value": DEFAULT_OCCURENCES,
          "valid": false,
          "errorText": ""
        });
      }
    }
    else if (selectedValue === END_AFTER) {
      dispatch(CreateScheduleSlice.actions.setIsEndAfterEnabled(true));
      dispatch(CreateScheduleSlice.actions.setIsEndByEnabled(false));
      setrecurrenceRangeEndDate({
        "value": "",
        "valid": false,
        "errorText": ""
      });
      dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
        ...scheduling.schedulingDetails,
        recurrenceNoOfOccurrences: noOfOccurences.value.toString(),
        recurrenceRangeEnd: ""
      }));
    }
  };

  /**
   * EndBy (recurrenceRangeEnd) Date Time on change event
   * Validation - EndBy date should be greater than start date and should be valid date
   * Selected date will be updated in redux
   */
  const onEndByChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const selectedDate = new Date(e.currentTarget.value);
    if (selectedDate) {
      const { isInvalid, validationText } =
        IsDateValid(startDateToCalculateEnd, translate, END_DATE, selectedDate.toString());
      setrecurrenceRangeEndDate({
        "value": selectedDate,
        "valid": isInvalid,
        "errorText": validationText
      });

      /*  Temporary if condition will be removed once the datepicker component is ready in emis ui,
      as this validation will be within the component. */
      if(getCustomMaxDateValidation(selectedDate)) {
        setrecurrenceRangeEndDate({
          "value": selectedDate,
          "valid": false,
          "errorText": translate("Scheduling.ScheduleErrors.MaxYearValidation")
        });
      }

      dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
        ...scheduling.schedulingDetails,
        recurrenceRangeEnd: getFormattedDate(selectedDate, false)
      }));
    }
  };

  /**
   * To handle toggling switch while create
   * @param selectedValue on/off
   */
  const handleOnToggle = (selectedValue: boolean): void => {
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
      ...scheduling.schedulingDetails,
      activeInLive: selectedValue
    }));
  };

  return (
    <ScheduleCreationFormView
      onFieldChange={onFieldChange}
      onRecurTypeChange={onRecurTypeChange}
      onTimeChange={onTimeChange}
      onStartDateSelection={onStartDateSelection}
      onEndByRadioChange={onEndByRadioChange}
      onEndByChange={onEndByChange}
      schedulingDetails={scheduling.schedulingDetails}
      recurrenceRangeStartDate={recurrenceRangeStartDate}
      recurrenceRangeEndDate={recurrenceRangeEndDate}
      noOfOccurences={noOfOccurences}
      scheduling={scheduling}
      isValidScheduleName={isValidSchedule}
      defaultEndDateValue={defaultEndDateValue}
      onToggle={handleOnToggle}
    />
  );
};

export default ScheduleCreationForm;
